import { findFavourite } from './favourite.ts'
import { rankTargets, type Frecency, type Target } from './targets.ts'

export type PickerRow =
  | { readonly kind: 'section'; readonly key: string; readonly label: string }
  | {
      readonly kind: 'group'
      readonly key: string
      readonly id: string
      readonly name: string
      readonly icon: string
      readonly color: string
      readonly depth: number
      readonly collapsed: boolean
    }
  | {
      readonly kind: 'option'
      readonly key: string
      readonly target: Target
      readonly depth: number
      readonly disabledReason: string | undefined
      /** A selectable account that also has children shown beneath it. */
      readonly expandable: boolean
      readonly collapsed: boolean
    }
  | { readonly kind: 'action'; readonly key: string; readonly action: PickerAction }

export type PickerAction =
  | { readonly type: 'create'; readonly name: string }
  | { readonly type: 'existing'; readonly target: Target }
  | { readonly type: 'new' }

export interface PickerRowLabels {
  readonly favourite: string
  readonly recent: string
  readonly accounts: string
  readonly contacts: string
}

export interface PickerRowOptions {
  readonly query: string
  readonly collapsed: ReadonlySet<string>
  /** False lists accounts flat, each under its full path. */
  readonly grouped: boolean
  readonly frecency: ReadonlyMap<string, Frecency>
  /** Listed first when the picker offers it and it is selectable; ignored while searching. */
  readonly favouriteId?: string | undefined
  readonly labels: PickerRowLabels
  /** Offers creating a contact: a row for the typed name and a permanent "new contact" row. */
  readonly creatable?: boolean
  readonly unavailable?: ((target: Target) => string | undefined) | undefined
}

const RECENT_LIMIT = 5

export function isShortcutKey(key: string): boolean {
  return key.startsWith('recent:') || key.startsWith('favourite:')
}

function matches(target: Target, needle: string): boolean {
  return needle === '' || target.path.toLowerCase().includes(needle)
}

/** A fixture account id can coincide across ledgers (every ledger seeds the same catalogue),
 * so a row key built from the id alone collides once `includeOtherLedgers` is on - Vue then
 * reconciles two different rows onto one, misattributing clicks. Fold the ledger in too. */
function targetKey(target: Target): string {
  return target.ledgerId === undefined ? target.id : `${target.ledgerId}:${target.id}`
}

/** Grouped rows for the picker: account groups as headers, contacts in their own section. A search
 * keeps every matching account's ancestors; a group whose name matches keeps all its children. */
export function buildPickerRows(targets: readonly Target[], options: PickerRowOptions): PickerRow[] {
  const needle = options.query.trim().toLowerCase()
  const searching = needle !== ''
  const unavailable = options.unavailable
  const accountTargets = targets.filter((target) => target.source === 'account')
  const accountIds = new Set(accountTargets.map((target) => target.id))
  const parentIds = new Set(
    accountTargets.flatMap((target) =>
      (target.ancestors ?? []).map((ancestor) => ancestor.id).filter((id) => accountIds.has(id)),
    ),
  )
  const visibleIds = new Set<string>()
  for (const target of accountTargets) {
    if (!matches(target, needle)) continue
    visibleIds.add(target.id)
    for (const ancestor of target.ancestors ?? []) visibleIds.add(ancestor.id)
  }
  const visible = targets.filter((target) => target.source === 'contact' && matches(target, needle))
  const accounts = accountTargets.filter((target) => visibleIds.has(target.id))
  const contacts = visible.sort((a, b) => a.leaf.localeCompare(b.leaf))
  const rows: PickerRow[] = []
  const optionRow = (target: Target, depth: number, key: string): PickerRow => ({
    kind: 'option',
    key,
    target,
    depth: options.grouped ? depth : 0,
    disabledReason: unavailable?.(target),
    expandable: options.grouped && parentIds.has(target.id) && !isShortcutKey(key),
    collapsed: !searching && options.collapsed.has(target.id),
  })

  if (!searching) {
    const favourite = findFavourite(
      accountTargets,
      options.favouriteId,
      (target) => unavailable?.(target) === undefined,
    )
    if (favourite !== undefined) {
      rows.push({ kind: 'section', key: 'section:favourite', label: options.labels.favourite })
      rows.push(optionRow(favourite, 0, `favourite:${targetKey(favourite)}`))
    }
    const recent = rankTargets(targets, options.frecency)
      .filter((entry) => entry.frecency !== undefined)
      .slice(0, RECENT_LIMIT)
    if (recent.length > 0) {
      rows.push({ kind: 'section', key: 'section:recent', label: options.labels.recent })
      for (const { target } of recent) rows.push(optionRow(target, 0, `recent:${targetKey(target)}`))
    }
  }

  const hasContacts = targets.some((target) => target.source === 'contact')
  if (accounts.length > 0) {
    if (hasContacts || rows.length > 0) {
      rows.push({ kind: 'section', key: 'section:accounts', label: options.labels.accounts })
    }
    let chain: readonly { id: string }[] = []
    for (const target of accounts) {
      const ancestors = options.grouped ? (target.ancestors ?? []) : []
      let shared = 0
      while (
        shared < chain.length &&
        shared < ancestors.length &&
        chain[shared]!.id === ancestors[shared]!.id
      ) {
        shared += 1
      }
      for (let depth = shared; depth < ancestors.length; depth += 1) {
        const group = ancestors[depth]!
        if (accountIds.has(group.id)) continue
        const hiddenByCollapse =
          !searching && ancestors.slice(0, depth).some((ancestor) => options.collapsed.has(ancestor.id))
        if (!hiddenByCollapse) {
          // A nested group's own id can repeat across ledgers too (its account ancestor is
          // from the same seeded catalogue) - key by the whole chain down to it, not just its id.
          const chainKey = ancestors
            .slice(0, depth + 1)
            .map((ancestor) => ancestor.id)
            .join('>')
          rows.push({
            kind: 'group',
            key: `group:${chainKey}`,
            id: group.id,
            name: group.name,
            icon: group.icon,
            color: group.color,
            depth,
            collapsed: !searching && options.collapsed.has(group.id),
          })
        }
      }
      chain = ancestors
      const hidden = !searching && ancestors.some((ancestor) => options.collapsed.has(ancestor.id))
      if (!hidden) rows.push(optionRow(target, ancestors.length, `option:${targetKey(target)}`))
    }
  }

  if (contacts.length > 0 || options.creatable === true) {
    rows.push({ kind: 'section', key: 'section:contacts', label: options.labels.contacts })
    for (const target of contacts) rows.push(optionRow(target, 0, `option:${targetKey(target)}`))
  }
  if (options.creatable === true) {
    const typed = options.query.trim()
    if (typed !== '') {
      const existing = targets.find(
        (target) => target.source === 'contact' && target.leaf.trim().toLowerCase() === typed.toLowerCase(),
      )
      rows.push({
        kind: 'action',
        key: 'action:create',
        action:
          existing === undefined ? { type: 'create', name: typed } : { type: 'existing', target: existing },
      })
    }
    rows.push({ kind: 'action', key: 'action:new', action: { type: 'new' } })
  }
  return rows
}

export type NavigableRow = Extract<PickerRow, { kind: 'option' | 'action' }>

export function isNavigable(row: PickerRow): row is NavigableRow {
  return row.kind === 'action' || (row.kind === 'option' && row.disabledReason === undefined)
}
